import type { OverlayRef } from '@angular/cdk/overlay';
import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import type { TemplateRef } from '@angular/core';
import { Component, DestroyRef, Input, ViewChild, ViewContainerRef, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { PushPipe } from '@ngrx/component';
import { asyncScheduler, BehaviorSubject, delay, filter, lastValueFrom, observeOn } from 'rxjs';

import { DrawerResizeDirective } from '../../directives/drawer-resize.directive';
import type { DrawerContainerWidthConfig } from '../../types';
import { DrawerState } from '../../types';
import { DrawerComponent } from '../drawer/drawer.component';
import { DrawerDragHandleComponent } from '../drawer-drag-handle/drawer-drag-handle.component';

@Component({
  selector: 'rt-drawer-container',
  templateUrl: './drawer-container.component.html',
  styleUrls: ['./drawer-container.component.less'],
  imports: [PushPipe, DrawerResizeDirective, DrawerComponent, DrawerDragHandleComponent],
})
export class DrawerContainerComponent {
  public static animationTime: number = 300;

  @Input() public widthConfig: DrawerContainerWidthConfig;

  public drawerSizeChanges = output<number>();

  @ViewChild('overlayTemplate', { static: true }) public overlayTemplate: TemplateRef<any>;
  public drawerState$ = new BehaviorSubject<DrawerState>(DrawerState.Close);

  // Slide-in animation should only execute after the component is drawn on the overlay - hence the asyncScheduler.
  public visibilityTransition$ = this.drawerState$.pipe(observeOn(asyncScheduler));
  public DrawerState = DrawerState;

  private overlayRef: OverlayRef;

  private close$ = this.drawerState$.pipe(
    filter(() => !!this.overlayRef),
    filter(state => state === DrawerState.Close),
    delay(DrawerContainerComponent.animationTime),
  );

  private open$ = this.drawerState$.pipe(
    filter(() => !!this.overlayRef),
    filter(state => state === DrawerState.Open),
  );

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private destroyRef: DestroyRef,
  ) {}

  public ngOnInit() {
    this.createContainerOverlay();
    this.watchOverlayStateChanges();
  }

  public ngOnDestroy(): void {
    this.detachTemplatePortal();
  }

  public toggle(): Promise<DrawerState> {
    this.drawerState$.next(
      this.drawerState$.value === DrawerState.Open ? DrawerState.Close : DrawerState.Open,
    );
    return lastValueFrom(this.drawerState$);
  }

  public open(): Promise<DrawerState> {
    this.drawerState$.next(DrawerState.Open);
    return lastValueFrom(this.drawerState$);
  }

  public close(): Promise<DrawerState> {
    this.drawerState$.next(DrawerState.Close);
    return lastValueFrom(this.drawerState$);
  }

  private createContainerOverlay() {
    const overlayConfig = new OverlayConfig({
      hasBackdrop: false,
      backdropClass: 'overlay',
      positionStrategy: this.overlay.position().global().right('0'),
    });
    this.overlayRef = this.overlay.create(overlayConfig);
  }

  private attachTemplatePortal() {
    this.overlayRef.attach(new TemplatePortal(this.overlayTemplate, this.viewContainerRef));
  }

  private detachTemplatePortal() {
    this.overlayRef.detach();
  }

  private watchOverlayStateChanges() {
    this.close$
      .pipe(
        observeOn(asyncScheduler),
        delay(DrawerContainerComponent.animationTime),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.detachTemplatePortal();
      });

    this.open$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.attachTemplatePortal();
    });
  }
}
